import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TextInput } from 'react-native';

import './FormInput.scss';

/**
 * Renders a stylized input with built in hooks for error states
 * @example
 * import FormInput from './FormInput';
 *
 * const MyComponent = () => {
 *   return (
 *     <FormInput
 *         label="Enter Email"
 *         name="email"
 *         error={this.props.emailError}
 *         value={this.props.email}
 *         onChange={this.updateEmail}
 *         onBlur={this.onBlur}
 *         placeholder="me@scissorcut.com"
 *      />
 *   );
 * }
 */
const FormInput = React.forwardRef((props, ref) => {
  const {
    name,
    label,
    type,
    error,
    hideErrorMessage,
    value,
    placeholder,
    ariaLabel,
    onChange,
    onBlur,
    onFocus,
    className,
    appearance,
    inputClassName,
    testID,
    labelStyle,
    inputStyle,
    ...restProps
  } = props;

  return (
    <label
      className={classNames(
        'form-input-component',
        `form-input-component-${appearance || 'normal'}`,
        className,
        { 'input-error': error },
      )}
      htmlFor={name}
      style={labelStyle}
    >
      {label}
      <TextInput
        multiline={type === 'textarea'}
        secureTextEntry={type === 'password'}
        className={classNames(inputClassName, { 'input-error': error })}
        type={type}
        name={name}
        value={value || ''}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        ref={ref}
        id={name}
        data-testid={testID}
        style={inputStyle}
        {...restProps}
      />
      {error && !hideErrorMessage && (
        <span className="error">
          {error}
        </span>
      )}
    </label>
  );
});

FormInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  type: PropTypes.string,
  error: PropTypes.string,
  hideErrorMessage: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  className: PropTypes.string,
  appearance: PropTypes.oneOf([null, 'normal', 'flat', 'updated-font']),
  inputClassName: PropTypes.string,
  testID: PropTypes.string,
  labelStyle: PropTypes.object,
  inputStyle: PropTypes.object,
};

FormInput.defaultProps = {
  name: '',
  label: '',
  value: '',
  placeholder: '',
  ariaLabel: '',
  type: 'text',
  error: '',
  hideErrorMessage: false,
  onChange: () => { },
  onBlur: () => { },
  onFocus: () => { },
  className: '',
  appearance: 'normal',
  inputClassName: '',
  testID: 'FormInput',
  labelStyle: {},
  inputStyle: {},
};

export default FormInput;
